import { faSpinner } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQuery } from "@tanstack/react-query";
import type { CSSProperties } from "react";
import {
    useEmissionRecordsApiClient,
    useNondetectsApiClient,
} from "../../../../../../hooks";
import { formatDateTime } from "../../../../../../utils/dates";

const Loading = () => (
    <div className="w-full h-full p-1 flex items-center justify-center">
        <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
    </div>
);

const EmissionHover = ({
    emissionId,
    groupedEmissionData,
}: {
    emissionId: string | "grouped";
    groupedEmissionData?: {
        meanDate: Date;
        total: number;
        providerName: string;
        numberOfGroupedEmissions: number;
    };
}) => {
    const emissionApiClient = useEmissionRecordsApiClient();
    const emissionQuery = useQuery({
        queryKey: ["emissionRecord", emissionId],
        queryFn: async () => {
            const response = await emissionApiClient.emissionRecordsRetrieve({
                id: emissionId,
            });
            return response;
        },
        enabled: emissionId !== "grouped",
        refetchOnWindowFocus: false,
        retry: false,
    });

    if (emissionId === "grouped") {
        if (!groupedEmissionData) return null;
        return (
            <div>
                <p className="font-bold mb-2">
                    {groupedEmissionData.providerName} (grouped)
                </p>
                <p className="mb-1">
                    {formatDateTime(groupedEmissionData.meanDate)}
                </p>
                <p className="mb-1">
                    Sum: {(groupedEmissionData.total / 1000).toFixed(2)}
                    {" kg/h"}
                </p>
                <p className="mb-1">
                    {groupedEmissionData.numberOfGroupedEmissions} emissions
                    grouped.
                </p>
            </div>
        );
    }

    if (emissionQuery.isLoading || !emissionQuery.data) {
        return <Loading />;
    }

    return (
        <div>
            <p className="font-bold mb-2">{emissionQuery.data.providerName}</p>
            <p className="mb-1">
                {formatDateTime(
                    emissionQuery.data.dataPoint.detectionTimestamp,
                )}
            </p>
            {emissionQuery.data.dataPoint.secondaryDataSource && (
                <p className="mb-1">
                    {emissionQuery.data.dataPoint.secondaryDataSource}
                </p>
            )}
            {emissionQuery.data.dataPoint.detectedRate && (
                <p className="mb-1">
                    {(emissionQuery.data.dataPoint.detectedRate / 1000).toFixed(
                        2,
                    )}
                    {" ± "}
                    {(
                        (emissionQuery.data.dataPoint.detectedRateUncertainty ??
                            0) / 1000
                    ).toFixed(2)}
                    {" kg/h"}
                </p>
            )}
            {!emissionQuery.data.dataPoint.detectedRate &&
                emissionQuery.data.dataPoint.concentration && (
                    <p className="mb-1">
                        {emissionQuery.data.dataPoint.concentration.toFixed(2)}
                        {" ± "}
                        {emissionQuery.data.dataPoint.concentrationUncertainty?.toFixed(
                            2,
                        )}
                        {" ppm*m"}
                    </p>
                )}
            {emissionQuery.data.dataPoint.data?.bridger_equipment_type && (
                <p className="mb-1">
                    {emissionQuery.data.dataPoint.data?.bridger_equipment_type}
                </p>
            )}
        </div>
    );
};

const NonDetectHover = ({ nonDetectId }: { nonDetectId: string }) => {
    const nonDetectsApiClient = useNondetectsApiClient();
    const nonDetectQuery = useQuery({
        queryKey: ["nondetect", nonDetectId],
        queryFn: async () => {
            const response =
                await nonDetectsApiClient.nondetectsSitenondetectRetrieve({
                    id: nonDetectId,
                });
            return response;
        },
        enabled: true,
        refetchOnWindowFocus: false,
        retry: false,
    });

    if (nonDetectQuery.isLoading || !nonDetectQuery.data) {
        return <Loading />;
    }

    return (
        <div>
            <p className="font-bold">{nonDetectQuery.data.dataProvider.name}</p>
            <p className="font-bold mb-1">(Non Detect Observation)</p>
            <p className="mb-1">
                {formatDateTime(nonDetectQuery.data.timestamp)}
            </p>
            {nonDetectQuery.data.secondaryDataSource && (
                <p className="mb-1">
                    {nonDetectQuery.data.secondaryDataSource}
                </p>
            )}
            {nonDetectQuery.data.detectionLimit !== null && (
                <p className="mb-1">
                    {(nonDetectQuery.data.detectionLimit / 1000).toFixed(2)}
                    {" kg/h"}
                </p>
            )}
        </div>
    );
};

export const EmissionPlotHover = ({
    emissionId,
    groupedEmissionData,
    nonDetectId,
    style,
}: {
    emissionId?: string;
    groupedEmissionData?: {
        meanDate: Date;
        total: number;
        providerName: string;
        numberOfGroupedEmissions: number;
    };
    nonDetectId?: string;
    style?: CSSProperties;
}) => {
    if (!emissionId && !nonDetectId) {
        return null;
    }

    return (
        <div
            className="bg-white rounded drop-shadow border p-1 text-xs"
            style={style}
        >
            {emissionId && (
                <EmissionHover
                    emissionId={emissionId}
                    groupedEmissionData={groupedEmissionData}
                />
            )}
            {nonDetectId && <NonDetectHover nonDetectId={nonDetectId} />}
        </div>
    );
};
